// Achievements: small milestone badges with client-side unlock tracking.
// Each achievement has an id, name, description, and a condition function.
// After a run, call `checkAchievements(ctx)` — returns any newly unlocked.
import { getStreak } from "@/lib/streak";
import { getIsPro } from "@/lib/pro";
import { getHighScore } from "@/lib/highscore";

const STORAGE_KEY = "ppe_achievements_v1";

// Icon names map to lucide-react icons in the UI
export const ACHIEVEMENTS = [
  {
    id: "first_escape",
    name: "FIRST ESCAPE",
    description: "Reach an exit for the first time",
    icon: "DoorOpen",
    check: (c) => c.won === true,
  },
  {
    id: "score_1k",
    name: "SCORE HUNTER",
    description: "Reach 1,000 in any run",
    icon: "Target",
    check: (c) => (c.score || 0) >= 1000,
  },
  {
    id: "score_5k",
    name: "SHARPSHOOTER",
    description: "Reach 5,000 in any run",
    icon: "Crosshair",
    check: (c) => (c.score || 0) >= 5000,
  },
  {
    id: "marathoner",
    name: "MARATHONER",
    description: "Survive 60 seconds in any run",
    icon: "Timer",
    check: (c) => (c.time || 0) >= 60,
  },
  {
    id: "underground_pro",
    name: "UNDERGROUND EXPERT",
    description: "Score 3,000+ underground",
    icon: "Cpu",
    check: (c) => (c.level === "underground" && (c.score || 0) >= 3000) || (getHighScore().underground || 0) >= 3000,
  },
  {
    id: "rooftop_win",
    name: "ROOFTOP RIDER",
    description: "Escape the rooftop stage",
    icon: "Wind",
    check: (c) => c.level === "rooftop" && c.won === true,
  },
  {
    id: "highway_win",
    name: "HIGHWAY CROSSER",
    description: "Escape the highway stage",
    icon: "Truck",
    check: (c) => c.level === "highway" && c.won === true,
  },
  {
    id: "snowstorm_win",
    name: "STORM SURVIVOR",
    description: "Escape the snowstorm stage",
    icon: "Snowflake",
    check: (c) => c.level === "snowstorm" && c.won === true,
  },
  {
    id: "tunnels_win",
    name: "TUNNEL RUNNER",
    description: "Escape the tunnels stage",
    icon: "GitBranch",
    check: (c) => c.level === "tunnels" && c.won === true,
  },
  {
    id: "ghost_beaten",
    name: "GHOST BEATER",
    description: "Beat today's daily ghost",
    icon: "Ghost",
    check: (c) => c.beatGhost === true,
  },
  {
    id: "streak_3",
    name: "STREAK STARTER",
    description: "3-day daily streak",
    icon: "Flame",
    check: () => getStreak().streak >= 3,
  },
  {
    id: "streak_7",
    name: "IRON WEEK",
    description: "7-day daily streak",
    icon: "Award",
    check: () => getStreak().streak >= 7,
  },
  {
    id: "pro_member",
    name: "PRO PLAYER",
    description: "Unlock full Pro access",
    icon: "Crown",
    check: () => getIsPro(),
  },
];

function read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function write(v) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {}
}

export function getUnlockedIds() {
  return Object.keys(read());
}

export function getUnlockedCount() {
  return getUnlockedIds().length;
}

// ctx = { score, time, level, won, beatGhost, isPro }
// Returns array of newly unlocked achievement objects.
export function checkAchievements(ctx = {}) {
  const store = read();
  const newly = [];
  for (const a of ACHIEVEMENTS) {
    if (store[a.id]) continue;
    try {
      if (a.check(ctx)) {
        store[a.id] = new Date().toISOString();
        newly.push(a);
      }
    } catch {}
  }
  if (newly.length > 0) write(store);
  return newly;
}

// Call once on menu load so cosmetic checks (streak_7, pro_member) update automatically.
export function refreshPassiveAchievements() {
  return checkAchievements({});
}
